import { interpolate, resolveCreateCommonText } from '../translations.js';

const ELLIPSIS_CYCLE = ['', '.', '..', '...'];
const ELLIPSIS_INTERVAL_MS = 450;
const FLAVOR_INTERVAL_MS = 9000;
const SLOW_PREVIEW_KAOMOJI = '＼(°□°)／';

const BROWSE_PHASES = [
  { key: 'ai_progress.browse.url_detected', afterMs: 0 },
  { key: 'ai_progress.browse.reading', afterMs: 5000 },
  { key: 'ai_progress.browse.writing_summary', afterMs: 15000 },
  { key: 'ai_progress.browse.taking_longer', afterMs: 23000 },
];

const GENERATE_PHASES = {
  character: [
    { key: 'ai_progress.generate.processing', afterMs: 0 },
    { key: 'ai_progress.generate.writing_character', afterMs: 5000 },
    { key: 'ai_progress.generate.almost_done', afterMs: 10000 },
    { key: 'ai_progress.generate.taking_longer', afterMs: 15000 },
  ],
  scenario: [
    { key: 'ai_progress.generate.processing', afterMs: 0 },
    { key: 'ai_progress.generate.writing_scenario', afterMs: 5000 },
    { key: 'ai_progress.generate.almost_done', afterMs: 10000 },
    { key: 'ai_progress.generate.taking_longer', afterMs: 15000 },
  ],
};

export const SLOW_PREVIEW_FLAVOR_KEYS = [
  'ai_progress.slow_preview.tip_tags',
  'ai_progress.slow_preview.tip_separate',
  'ai_progress.slow_preview.tip_realism',
  'ai_progress.slow_preview.tip_public_media',
  'ai_progress.slow_preview.silly_often',
  'ai_progress.slow_preview.silly_gpu',
  'ai_progress.slow_preview.silly_sheep',
  'ai_progress.slow_preview.silly_judging',
  'ai_progress.slow_preview.silly_pixels',
  'ai_progress.slow_preview.silly_cinematic',
  'ai_progress.slow_preview.discord',
  'ai_progress.slow_preview.guide',
  'ai_progress.slow_preview.private_public',
  'ai_progress.slow_preview.same_farm',
];

const TEXT_FALLBACKS = {
  'ai_progress.browse.url_detected': 'URL detected. Trying to access content',
  'ai_progress.browse.reading': 'Reading website content',
  'ai_progress.browse.writing_summary': 'Writing a summary',
  'ai_progress.browse.taking_longer': 'Page browsing taking longer than expected',
  'ai_progress.generate.processing': 'Processing request',
  'ai_progress.generate.writing_character': 'Writing character data',
  'ai_progress.generate.writing_scenario': 'Writing scenario data',
  'ai_progress.generate.almost_done': 'Almost done',
  'ai_progress.generate.taking_longer': 'Taking longer than expected',
  'ai_progress.slow_preview.tip_tags': 'Tip: English Danbooru-style tags usually work best for appearance',
  'ai_progress.slow_preview.tip_separate': 'Tip: Separate looks and outfit—your future sprites will thank you',
  'ai_progress.slow_preview.tip_realism': 'Tip: Realism previews are lower-res on purpose so you are not stuck waiting',
  'ai_progress.slow_preview.tip_public_media': 'Tip: Everyone can add cool images and videos for public characters',
  'ai_progress.slow_preview.silly_often': '"So... you come here often?"',
  'ai_progress.slow_preview.silly_gpu': 'Hunting for spare GPUs… they keep hiding',
  'ai_progress.slow_preview.silly_sheep': 'Counting sheep… or sprites. Mostly sprites',
  'ai_progress.slow_preview.silly_judging': '{name} is pleased with your prompt',
  'ai_progress.slow_preview.silly_pixels': 'Brewing pixels. Please do not stir',
  'ai_progress.slow_preview.silly_cinematic': 'Absolute loading screen ＼(°□°)／',
  'ai_progress.slow_preview.discord': "You should join Kissandra's Discord!",
  'ai_progress.slow_preview.guide': 'Stuck on prompting? The guide is worth a look',
  'ai_progress.slow_preview.private_public': 'Private characters stay yours—public ones can climb the rankings',
  'ai_progress.slow_preview.same_farm': 'Slow preview uses the same infrastructure as full character sprites. Patience pays off',
  'generate_estimating': 'Estimating wait time…',
};

const resolveProgressText = (key, vars = null) => {
  const translated = resolveCreateCommonText(key, vars);
  if (translated) return translated;
  const fallback = TEXT_FALLBACKS[key] || key;
  if (vars && Object.keys(vars).length) return interpolate(fallback, vars);
  return fallback;
};

export const resolveAiProgressText = resolveProgressText;

const pickRandomKey = (keys, previousKey) => {
  if (!keys.length) return null;
  if (keys.length === 1) return keys[0];
  let next = keys[Math.floor(Math.random() * keys.length)];
  while (next === previousKey) {
    next = keys[Math.floor(Math.random() * keys.length)];
  }
  return next;
};

export const createAiProgressController = (statusEl) => {
  if (!statusEl) {
    return {
      startBrowsePhase: () => {},
      startGeneratePhase: () => {},
      startFlavorRotation: () => {},
      clear: () => {},
    };
  }

  const textEl = statusEl.querySelector('.ai-progress-status__text') || statusEl;
  const ellipsisEl = statusEl.querySelector('.ai-progress-status__ellipsis');
  let phaseTimeouts = [];
  let ellipsisInterval = null;
  let flavorInterval = null;
  let ellipsisIndex = 0;
  let lastFlavorKey = null;

  const stopEllipsis = () => {
    if (ellipsisInterval) {
      clearInterval(ellipsisInterval);
      ellipsisInterval = null;
    }
  };

  const stopPhaseTimers = () => {
    phaseTimeouts.forEach((id) => clearTimeout(id));
    phaseTimeouts = [];
  };

  const stopFlavorRotation = () => {
    if (flavorInterval) {
      clearInterval(flavorInterval);
      flavorInterval = null;
    }
    lastFlavorKey = null;
  };

  const startEllipsis = () => {
    stopEllipsis();
    if (!ellipsisEl) return;
    ellipsisIndex = 0;
    ellipsisEl.textContent = ELLIPSIS_CYCLE[0];
    ellipsisInterval = setInterval(() => {
      ellipsisIndex = (ellipsisIndex + 1) % ELLIPSIS_CYCLE.length;
      ellipsisEl.textContent = ELLIPSIS_CYCLE[ellipsisIndex];
    }, ELLIPSIS_INTERVAL_MS);
  };

  const setMessage = (text) => {
    if (textEl) {
      const kaomojiAt = text.indexOf(SLOW_PREVIEW_KAOMOJI);
      if (kaomojiAt === -1) {
        textEl.textContent = text;
      } else {
        textEl.replaceChildren();
        if (kaomojiAt > 0) textEl.append(text.slice(0, kaomojiAt));
        const kaomojiEl = document.createElement('span');
        kaomojiEl.className = 'ai-progress-status__kaomoji';
        kaomojiEl.textContent = SLOW_PREVIEW_KAOMOJI;
        textEl.append(kaomojiEl);
        const after = text.slice(kaomojiAt + SLOW_PREVIEW_KAOMOJI.length);
        if (after) textEl.append(after);
      }
    }
    statusEl.classList.remove('is-hidden');
    startEllipsis();
  };

  const runPhases = (phases) => {
    stopPhaseTimers();
    stopFlavorRotation();
    phases.forEach(({ key, afterMs }) => {
      const id = setTimeout(() => setMessage(resolveProgressText(key)), afterMs);
      phaseTimeouts.push(id);
    });
  };

  const startBrowsePhase = () => {
    stopPhaseTimers();
    runPhases(BROWSE_PHASES);
  };

  const startGeneratePhase = ({ variant = 'character' } = {}) => {
    stopPhaseTimers();
    const phases = GENERATE_PHASES[variant] || GENERATE_PHASES.character;
    runPhases(phases);
  };

  const startFlavorRotation = (keys = SLOW_PREVIEW_FLAVOR_KEYS, { intervalMs = FLAVOR_INTERVAL_MS, vars = null } = {}) => {
    stopPhaseTimers();
    stopFlavorRotation();
    if (!keys.length) return;

    const showNext = () => {
      lastFlavorKey = pickRandomKey(keys, lastFlavorKey);
      setMessage(resolveProgressText(lastFlavorKey, vars));
    };

    showNext();
    flavorInterval = setInterval(showNext, intervalMs);
  };

  const clear = () => {
    stopPhaseTimers();
    stopFlavorRotation();
    stopEllipsis();
    if (textEl) textEl.textContent = '';
    if (ellipsisEl) ellipsisEl.textContent = '';
    statusEl.classList.add('is-hidden');
  };

  return { startBrowsePhase, startGeneratePhase, startFlavorRotation, clear };
};
